type Box = { x: number; y: number; w: number; h: number }
export type LabelRack = Box & { id: number; labelWidth: number; labelHeight: number; priority?: boolean; showLabel?: boolean }
export type LabelSegment = { x1: number; y1: number; x2: number; y2: number }
export type CalculatorLabel = Box & { id: number; external: boolean; anchorX: number; anchorY: number }

function overlaps(a: Box, b: Box, gap = 0): boolean {
  return a.x < b.x + b.w + gap && a.x + a.w + gap > b.x && a.y < b.y + b.h + gap && a.y + a.h + gap > b.y
}

// Slab intersection includes diagonal, vertical and zero-length segments.
function crosses(segment: LabelSegment, box: Box): boolean {
  let from = 0, to = 1
  for (const [start, delta, min, max] of [
    [segment.x1, segment.x2 - segment.x1, box.x - 6, box.x + box.w + 6],
    [segment.y1, segment.y2 - segment.y1, box.y - 6, box.y + box.h + 6],
  ]) {
    if (delta === 0) { if (start < min || start > max) return false; continue }
    const a = (min - start) / delta, b = (max - start) / delta
    from = Math.max(from, Math.min(a, b))
    to = Math.min(to, Math.max(a, b))
    if (from > to) return false
  }
  return true
}

export function placeCalculatorLabels(racks: LabelRack[], segments: LabelSegment[], viewport: Box, reserved: Box[] = []): CalculatorLabel[] {
  const placed: CalculatorLabel[] = []
  // ponytail: bounded greedy search is O(n*(n+segments)) on visible racks; use a spatial index if profiled dense halls need it.
  for (const rack of [...racks].sort((a, b) => Number(Boolean(b.priority)) - Number(Boolean(a.priority)))) {
    if (rack.showLabel === false) continue
    const w = rack.labelWidth, h = rack.labelHeight, cx = rack.x + rack.w / 2, cy = rack.y + rack.h / 2
    const candidates = [
      { x: cx - w / 2, y: rack.y + 6, w, h, external: false },
      { x: cx - w / 2, y: rack.y + rack.h - h - 6, w, h, external: false },
    ]
    for (let tier = 0; tier < 5; tier++) {
      const gap = 10 + tier * (h + 6)
      for (const shift of [0, -w / 2, w / 2]) {
        candidates.push({ x: cx - w / 2 + shift, y: rack.y - gap - h, w, h, external: true },
          { x: cx - w / 2 + shift, y: rack.y + rack.h + gap, w, h, external: true })
      }
      candidates.push({ x: rack.x - gap - w, y: cy - h / 2, w, h, external: true },
        { x: rack.x + rack.w + gap, y: cy - h / 2, w, h, external: true })
    }
    const box = candidates.find(candidate => {
      if (candidate.x < viewport.x || candidate.y < viewport.y || candidate.x + w > viewport.x + viewport.w || candidate.y + h > viewport.y + viewport.h) return false
      if (!candidate.external && (candidate.x < rack.x + 4 || candidate.x + w > rack.x + rack.w - 4 || candidate.y + h > rack.y + rack.h - 4)) return false
      return !segments.some(segment => crosses(segment, candidate)) && !reserved.some(other => overlaps(candidate, other)) &&
        !placed.some(other => overlaps(candidate, other, 4)) && !racks.some(other => other.id !== rack.id && overlaps(candidate, other, 4))
    })
    if (!box) continue
    placed.push({ ...box, id: rack.id, anchorX: Math.max(rack.x, Math.min(rack.x + rack.w, box.x + w / 2)),
      anchorY: Math.max(rack.y, Math.min(rack.y + rack.h, box.y + h / 2)) })
  }
  return placed
}
